import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, WhatsappLogo, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "../App";

const WelcomeVideo = ({ goal, onClose }) => {
  const [showButton, setShowButton] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);
  const hasTrackedView = useRef(false);
  const hasTrackedComplete = useRef(false);

  // Video configs based on goal
  const videoConfig = {
    emagrecimento: {
      videoId: "e85eaa8d-55d3-4e4d-a4e1-12546f6854fd",
      buttonTime: 38, // seconds
      buttonText: "Quero um acompanhamento",
      buttonLink: "https://wa.me/244939412587?text=Olá António! Quero um acompanhamento para emagrecimento.",
      isWhatsApp: true,
      trackingType: "emagrecimento"
    },
    ganhar: {
      videoId: "142c2f89-a8f9-454f-b1f0-510bce72b104",
      buttonTime: 47, // seconds
      buttonText: "Quero ter melhores resultados",
      buttonLink: "https://plano-anabolico.vercel.app/",
      isWhatsApp: false,
      trackingType: "ganhar"
    },
    manter: {
      videoId: "e85eaa8d-55d3-4e4d-a4e1-12546f6854fd",
      buttonTime: 38,
      buttonText: "Quero um acompanhamento",
      buttonLink: "https://wa.me/244939412587?text=Olá António! Quero um acompanhamento personalizado.",
      isWhatsApp: true,
      trackingType: "emagrecimento"
    }
  };

  // Get config for current goal (default to emagrecimento)
  const config = videoConfig[goal] || videoConfig.emagrecimento;

  // Track analytics event
  const trackEvent = async (eventType) => {
    try {
      await axios.post(`${API}/analytics/video-event`, {
        event_type: eventType,
        video_type: config.trackingType,
        user_goal: goal
      });
    } catch (error) {
      console.log("Analytics tracking:", eventType);
    }
  };

  useEffect(() => {
    // Track view on mount
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent('view');
    }

    // Start timer when component mounts
    intervalRef.current = setInterval(() => {
      setElapsedTime(prev => {
        const newTime = prev + 1;
        if (newTime >= config.buttonTime && !showButton) {
          setShowButton(true);
          // Track completion when button appears
          if (!hasTrackedComplete.current) {
            hasTrackedComplete.current = true;
            trackEvent('complete');
          }
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [config.buttonTime, showButton]);

  const handleButtonClick = () => {
    // Track click
    trackEvent('click');
    window.open(config.buttonLink, "_blank");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors z-10"
        data-testid="close-welcome-video"
      >
        <X size={28} weight="bold" />
      </button>

      {/* Video Container */}
      <div className="w-full max-w-2xl">
        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
          <iframe
            src={`https://player.mediadelivery.net/embed/701711/${config.videoId}?autoplay=true&loop=false&muted=false&preload=true&responsive=true`}
            loading="lazy"
            style={{
              border: 0,
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: "100%",
              borderRadius: "16px"
            }}
            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen;"
            allowFullScreen={true}
          />
        </div>

        {/* Animated CTA Button */}
        <AnimatePresence>
          {showButton && (
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              className="mt-6 flex justify-center"
            >
              <motion.button
                onClick={handleButtonClick}
                className="relative px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-lg rounded-full shadow-2xl flex items-center gap-3 overflow-hidden"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(251, 191, 36, 0.4)",
                    "0 0 40px rgba(251, 191, 36, 0.6)",
                    "0 0 20px rgba(251, 191, 36, 0.4)"
                  ]
                }}
                transition={{
                  boxShadow: {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
                data-testid="welcome-cta-button"
              >
                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  animate={{
                    x: ["-100%", "100%"]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    repeatDelay: 1
                  }}
                />
                
                {/* Icon */}
                {config.isWhatsApp ? (
                  <WhatsappLogo size={28} weight="fill" className="text-green-700" />
                ) : (
                  <ArrowRight size={28} weight="bold" />
                )}
                
                {/* Text */}
                <span className="relative z-10">{config.buttonText}</span>
                
                {/* Pulse ring */}
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-yellow-300"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.5, 0, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut"
                  }}
                />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skip text */}
        {!showButton && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-white/40 text-sm mt-6"
          >
            Assiste o vídeo completo...
          </motion.p>
        )}
      </div>
    </motion.div>
  );
};

export default WelcomeVideo;
